import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '../utils/swal';
import { LoadingInline } from './LoadingOverlay';
import './UserProfile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const UserProfile = ({ onBack, onViewTicket }) => {
  const { user, updateUser, logout } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'password' | 'reservations'

  // Profil formu
  const [profileForm, setProfileForm] = useState({ fullName: '', email: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  // Şifre formu
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passSaving, setPassSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('gg_access_token');

      // Profil bilgisi
      const res = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProfile(data);
      setProfileForm({ fullName: data.fullName, email: data.email, phone: data.phone });

      // Rezervasyonlar — Customer için backend zaten JWT'den userId alıp filtreler,
      // Admin/EventOwner için tüm liste gelir. Ayrı filtre gerekmez.
      const resRes = await fetch(`${API_URL}/api/reservations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resRes.ok) {
        const resList = await resRes.json();
        setReservations(resList);
      }
    } catch (err) {
      console.error('Profil yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const token = localStorage.getItem('gg_access_token');
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profileForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Güncelleme başarısız.');
      updateUser({ fullName: data.fullName, email: data.email });
      setProfile(prev => ({ ...prev, fullName: data.fullName, email: data.email, phone: profileForm.phone }));
      toast.success('Profil bilgileri güncellendi!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor!');
      return;
    }
    if (passForm.newPassword.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır.');
      return;
    }
    setPassSaving(true);
    try {
      const token = localStorage.getItem('gg_access_token');
      const res = await fetch(`${API_URL}/api/profile/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Şifre değiştirilemedi.');
      toast.success('Şifre başarıyla değiştirildi!');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPassSaving(false);
    }
  };

  const getStatusLabel = (status) => {
    const map = { 0: '⏳ Bekliyor', 1: '✅ Onaylandı', 2: '❌ İptal', 3: '✔️ Tamamlandı' };
    return map[status] ?? 'Bilinmiyor';
  };

  if (loading) return <LoadingInline message="Profil yükleniyor..." />;

  return (
    <div className="user-profile">
      <div className="profile-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <h2>👤 Profilim</h2>
        <button onClick={logout} className="logout-btn">Çıkış Yap</button>
      </div>

      {/* Kullanıcı özet kartı */}
      <div className="profile-card">
        <div className="profile-avatar">
          {profile?.fullName?.charAt(0).toUpperCase() ?? '?'}
        </div>
        <div className="profile-info">
          <h3>{profile?.fullName}</h3>
          <p>{profile?.email}</p>
          <span className={`role-badge role-${profile?.role?.toLowerCase()}`}>{profile?.role}</span>
        </div>
        <div className="profile-meta">
          <div className="meta-item">
            <span className="meta-label">Üyelik</span>
            <span className="meta-value">
              {profile?.creDate ? new Date(profile.creDate).toLocaleDateString('tr-TR') : '-'}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Son Giriş</span>
            <span className="meta-value">
              {profile?.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="profile-tabs">
        <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
          ✏️ Bilgilerimi Düzenle
        </button>
        <button className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`} onClick={() => setActiveTab('password')}>
          🔒 Şifre Değiştir
        </button>
        <button className={`tab-btn ${activeTab === 'reservations' ? 'active' : ''}`} onClick={() => setActiveTab('reservations')}>
          🎫 Rezervasyonlarım ({reservations.length})
        </button>
      </div>

      {/* Profil Düzenle */}
      {activeTab === 'profile' && (
        <div className="profile-section">
          <form onSubmit={handleProfileSave} className="profile-form">
            <div className="form-group">
              <label>Ad Soyad</label>
              <input
                type="text"
                value={profileForm.fullName}
                onChange={e => setProfileForm(p => ({ ...p, fullName: e.target.value }))}
                placeholder="Ad Soyad"
                required
              />
            </div>
            <div className="form-group">
              <label>E-posta</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                placeholder="ornek@email.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="text"
                value={profileForm.phone}
                onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="5551234567"
                maxLength={10}
              />
            </div>
            <button type="submit" className="save-btn" disabled={profileSaving}>
              {profileSaving ? '⏳ Kaydediliyor...' : '💾 Değişiklikleri Kaydet'}
            </button>
          </form>
        </div>
      )}

      {/* Şifre Değiştir */}
      {activeTab === 'password' && (
        <div className="profile-section">
          <form onSubmit={handlePasswordSave} className="profile-form">
            <div className="form-group">
              <label>Mevcut Şifre</label>
              <input
                type="password"
                value={passForm.currentPassword}
                onChange={e => setPassForm(p => ({ ...p, currentPassword: e.target.value }))}
                placeholder="Mevcut şifreniz"
                required
              />
            </div>
            <div className="form-group">
              <label>Yeni Şifre</label>
              <input
                type="password"
                value={passForm.newPassword}
                onChange={e => setPassForm(p => ({ ...p, newPassword: e.target.value }))}
                placeholder="En az 8 karakter"
                required
              />
            </div>
            <div className="form-group">
              <label>Yeni Şifre (Tekrar)</label>
              <input
                type="password"
                value={passForm.confirmPassword}
                onChange={e => setPassForm(p => ({ ...p, confirmPassword: e.target.value }))}
                placeholder="Yeni şifreyi tekrar girin"
                required
              />
            </div>
            <button type="submit" className="save-btn" disabled={passSaving}>
              {passSaving ? '⏳ Değiştiriliyor...' : '🔒 Şifreyi Değiştir'}
            </button>
          </form>
        </div>
      )}

      {/* Rezervasyonlarım */}
      {activeTab === 'reservations' && (
        <div className="profile-section">
          {reservations.length === 0 ? (
            <div className="no-reservations">
              <p>Henüz rezervasyonunuz bulunmuyor.</p>
            </div>
          ) : (
            <div className="reservations-list">
              {reservations.map(r => (
                <div key={r.id} className="reservation-item">
                  <div className="res-info">
                    <h4>{r.eventName}</h4>
                    <p className="res-date">
                      📅 {new Date(r.eventDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="res-seats">
                      🎫 {r.seats?.map(s => s.seatId).join(', ')}
                    </p>
                  </div>
                  <div className="res-meta">
                    <span className="res-status">{getStatusLabel(r.status)}</span>
                    <span className="res-amount">₺{r.totalAmount?.toFixed(2)}</span>
                    <span className="res-id">{r.id}</span>
                    {/* Ödeme alınmışsa bilet görüntüleme butonu */}
                    {r.paymentReceived && onViewTicket && (
                      <button
                        className="res-ticket-btn"
                        onClick={() => onViewTicket(r.id)}
                      >
                        🎟️ Biletimi Gör
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfile;